// src/pages/returnNote/ReturnNoteDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, RotateCcw, Package, Receipt, Star, Printer, XCircle, ShoppingBag, Store, FileText, User, Calendar } from 'lucide-react';
import { PageHeader, PrintPreviewModal, InfoCard } from '../../components/common';
import { getReturnNote, voidReturnNote } from '../../services/api/offlineSales/returnNotes';
import { getPaymentMethods } from '../../services/api/offlineSales/paymentMethods';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDateTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import { PaymentMethodIcon } from '../../utils/paymentMethodIcons';
import { exportReturnNotePDF } from '../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../utils/themeColors';
import ReturnNoteA4Document from './ReturnNoteA4Document';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './ReturnNoteDetailsPage.module.css';
import receiptStyles from '../offlineSales/components/OrderReceiptPrintModal.module.css';

const STATUS_COLORS = {
  completed: 'success',
  voided: 'error',
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const ReturnNoteDetailsPage = () => {
  const { returnNoteId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const { hasPermission } = usePermissions();

  const [returnNote, setReturnNote] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrint, setShowPrint] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);

  const loadReturnNote = async () => {
    try {
      setLoading(true);
      const result = await getReturnNote(returnNoteId);
      setReturnNote(result);
    } catch (err) {
      error(err?.response?.data?.message || t('returnNote.failedToLoadReturnNote'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReturnNote(); }, [returnNoteId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getPaymentMethods().then(setPaymentMethods).catch(() => setPaymentMethods([])); }, []);

  const handlePrint = () => setShowPrint(true);

  const handleVoid = async () => {
    setIsVoiding(true);
    try {
      await voidReturnNote(returnNoteId, { voidReason: voidReason.trim() || undefined });
      success(t('returnNote.returnNoteVoidedSuccessfully'));
      setShowVoidModal(false);
      setVoidReason('');
      loadReturnNote();
    } catch (err) {
      error(err?.response?.data?.message || t('returnNote.failedToVoidReturnNote'));
    } finally {
      setIsVoiding(false);
    }
  };

  const renderReceipt = () => (
    <div className={receiptStyles.receiptArea}>
      <h2 className={receiptStyles.receiptTitle}>{t('returnNote.returnNoteNumber')}</h2>
      <div className={receiptStyles.receiptMeta}>
        <div className={receiptStyles.receiptMetaRow}>
          <span>#</span>
          <span>{returnNote.returnNumber}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('common.created')}</span>
          <span>{new Date(returnNote.createdAt).toLocaleString()}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('returnNote.originalOrder')}</span>
          <span>#{returnNote.originalOrder?.orderNumber || t('common.na')}</span>
        </div>
      </div>
      <hr className={receiptStyles.receiptDivider} />
      <div className={receiptStyles.receiptItems}>
        {(returnNote.returnItems || []).map((item) => (
          <div key={item.id} className={receiptStyles.receiptItem}>
            <span className={receiptStyles.receiptItemName}>{item.Product?.name || t('common.na')}</span>
            <span className={receiptStyles.receiptItemQty}>×{item.quantityReturned}</span>
            <span className={receiptStyles.receiptItemTotal}>{formatCurrency(item.refundAmount)}</span>
          </div>
        ))}
      </div>
      <hr className={receiptStyles.receiptDivider} />
      <div className={`${receiptStyles.receiptTotalRow} ${receiptStyles.receiptGrandTotal}`}>
        <strong>{t('returnNote.totalRefundAmount')}</strong>
        <strong>{formatCurrency(returnNote.totalRefundAmount)}</strong>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!returnNote) {
    return <div className={styles.notFound}>{t('errors.notFound')}</div>;
  }

  const creditNoteUsed = (returnNote.creditNotes || []).some(cn => parseFloat(cn.appliedAmount) > 0);

  const renderContent = () => (
    <div className={styles.grid}>
      <div className={styles.column}>
        <InfoCard icon={RotateCcw} title={t('returnNote.returnNoteNumber')}>
          <div className={ps.infoGrid}>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('common.status')}</span>
              <span className={ps.infoValue}>
                <Badge type={STATUS_COLORS[returnNote.status]} label={t(`returnNote.status_${returnNote.status}`)} />
              </span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('returnNote.originalOrder')}</span>
              <span className={ps.infoValue}>
                <ShoppingBag size={14} />
                {returnNote.originalOrder?.orderNumber ? (
                  <Link to={`/offline-sales/orders/${returnNote.originalOrder.id}`}>{returnNote.originalOrder.orderNumber}</Link>
                ) : t('common.na')}
              </span>
            </div>
            {returnNote.originalOrder?.Shop?.name && (
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('common.shop')}</span>
                <span className={ps.infoValue}><Store size={14} />{returnNote.originalOrder.Shop.name}</span>
              </div>
            )}
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('returnNote.refundMethod')}</span>
              <span className={ps.infoValue}>
                {returnNote.refundMethod === 'cash' ? (
                  <PaymentMethodIcon
                    iconFileName={paymentMethods.find(m => m.key === 'cash')?.iconFileName}
                    label={t('returnNote.refundMethod_cash')}
                  />
                ) : (
                  <>
                    {returnNote.refundMethod === 'credit_note' ? <Receipt size={14} /> : <Star size={14} />}
                    {t(`returnNote.refundMethod_${returnNote.refundMethod}`)}
                  </>
                )}
              </span>
            </div>
            <div className={ps.divider} />
            <div className={`${ps.infoItem} ${ps.fullWidth}`}>
              <span className={ps.infoLabel}>{t('returnNote.totalRefundAmount')}</span>
              <span className={`${ps.infoValue} ${ps.infoValueLarge} ${ps.infoValueDanger}`}>{formatCurrency(returnNote.totalRefundAmount)}</span>
            </div>
            {parseFloat(returnNote.loyaltyPointsDeducted) > 0 && (
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('returnNote.loyaltyPointsDeducted')}</span>
                <span className={ps.infoValue}><Star size={14} />{returnNote.loyaltyPointsDeducted}</span>
              </div>
            )}
            {returnNote.reason && (
              <div className={`${ps.infoItem} ${ps.fullWidth}`}>
                <span className={ps.infoLabel}>{t('returnNote.reason')}</span>
                <p className={ps.notesText}>{returnNote.reason}</p>
              </div>
            )}
          </div>
        </InfoCard>

        {(returnNote.creditNotes || []).length > 0 && (
          <InfoCard icon={Receipt} title={t('returnNote.linkedCreditNotes')}>
            <div className={ps.tableWrapper}>
              <table className={ps.itemsTable}>
                <thead>
                  <tr>
                    <th>{t('returnNote.creditNote')}</th>
                    <th className={ps.alignRight}>{t('returnNote.creditNoteRemaining')}</th>
                    <th className={ps.alignRight}>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {returnNote.creditNotes.map((cn) => (
                    <tr key={cn.id}>
                      <td><Link className={ps.docLink} to={`/offline-sales/credit-notes/${cn.id}`}>{cn.creditNoteNumber}</Link></td>
                      <td className={`${ps.alignRight} ${ps.amountCell}`}>{formatCurrency(parseFloat(cn.amount) - parseFloat(cn.appliedAmount || 0))}</td>
                      <td className={ps.alignRight}>
                        <Badge
                          type={cn.status === 'cancelled' ? 'error' : cn.status === 'applied' ? 'success' : 'info'}
                          label={t(`creditNote.status_${cn.status}`)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </InfoCard>
        )}
      </div>

      <div className={styles.column}>
        <InfoCard icon={Package} title={t('returnNote.returnedItems')}>
          <div className={ps.tableWrapper}>
            <table className={ps.itemsTable}>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>#</th>
                  <th>{t('returnNote.item')}</th>
                  <th className={ps.alignRight}>{t('returnNote.returnQty')}</th>
                  <th className={ps.alignRight}>{t('returnNote.refundAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {(returnNote.returnItems || []).map((item, index) => (
                  <tr key={item.id}>
                    <td className={ps.itemIndexCell}>{index + 1}</td>
                    <td>
                      <div className={ps.productCellInner}>
                        <ProductThumb item={item} alt={item.Product?.name || ''} />
                        <div className={ps.productCell}>
                          <span className={ps.productName}>{item.Product?.name || t('common.na')}</span>
                          {item.ProductVariation?.sku && <span className={ps.productSku}>SKU: {item.ProductVariation.sku}</span>}
                        </div>
                      </div>
                    </td>
                    <td className={`${ps.alignRight} ${ps.amountCell}`}>{item.quantityReturned}</td>
                    <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{formatCurrency(item.refundAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </InfoCard>

        <InfoCard icon={FileText} title={t('ecomOrders.additionalInformation')}>
          <div className={ps.infoList}>
            <div className={ps.infoListItem}>
              <label>{t('returnNote.cashier')}</label>
              <p><User size={14} />{returnNote.cashier?.name || t('common.na')}</p>
            </div>
            <div className={ps.infoListItem}>
              <label>{t('common.created')}</label>
              <p><Calendar size={14} />{formatDateTime(returnNote.createdAt)}</p>
            </div>
          </div>
        </InfoCard>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={returnNote.returnNumber}
        icon={null}
        breadcrumbs={
          <button onClick={() => navigate('/offline-sales/return-notes')} className={styles.backBtn}>
            <ChevronLeft size={16} /> {t('returnNote.allReturnNotes')}
          </button>
        }
        actions={
          <div className={styles.headerActions}>
            <button className={styles.secondaryBtn} onClick={handlePrint}>
              <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
            </button>
            {returnNote.status === 'completed' && !creditNoteUsed && hasPermission('create_return_note') && (
              <button className={`${styles.secondaryBtn} ${ps.toneDanger}`} onClick={() => setShowVoidModal(true)}>
                <XCircle size={15} /> {t('returnNote.void')}
              </button>
            )}
          </div>
        }
      />

      {renderContent()}

      {showVoidModal && (
        <div className={styles.overlay}>
          <div className={styles.smallModal}>
            <div className={styles.modalHeader}>
              <h3>{t('returnNote.voidReturnNote')}</h3>
            </div>
            <div className={styles.modalBody}>
              <p>{t('returnNote.confirmVoidReturnNote')}</p>
              <label>{t('returnNote.voidReason')}</label>
              <textarea
                className={styles.select}
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder={t('returnNote.voidReasonPlaceholder')}
              />
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowVoidModal(false)} disabled={isVoiding}>
                {t('common.cancel')}
              </button>
              <button className={styles.primaryBtn} onClick={handleVoid} disabled={isVoiding}>
                {isVoiding ? t('common.loading') : t('returnNote.voidReturnNote')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrint && (
        <PrintPreviewModal
          title={`Return Note — ${returnNote.returnNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Return_Note_${returnNote.returnNumber || returnNote.id}.pdf`}
          onDownloadPdf={() => exportReturnNotePDF(returnNote, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderReceipt={renderReceipt}
          renderA4={() => <ReturnNoteA4Document returnNote={returnNote} />}
        />
      )}
    </div>
  );
};

export default ReturnNoteDetailsPage;
